import { useRouter } from 'next/router';
import { useContext, useEffect, useRef, useState } from 'react';
import {
  ReportIcon,
  CompanyIcon,
  DashboardTraditionalIcon,
  LogoutIcon,
  ReportCommonIcon,
  SetupIcon,
  SprayRecordIcon,
  AccountSettingIcon,
  InventoryManagementIcon,
  PurchaseRecordIcon,
} from '../icons';
import { CommonContentContext } from '@/utils/context/commonContent/CommonContentContext';
import { useAuth } from '@/utils/hooks/useAuth';
import RoleGuard from './RoleGuard';
import { UserRole } from '@/utils/interfaces/common/UserRole.enum';

const farmSetupPaths = [
  '/farm-setup',
  '/crop',
  '/variety',
  '/plant-spacing',
  '/block',
  '/machine',
  '/chemical',
  '/employee',
];

export const Navbar = () => {
  const router = useRouter();
  const navbar = useRef<HTMLDivElement>(null);
  const { logout } = useAuth();

  const { showMenu, navbarWithMenu, setNavbarWithMenu } = useContext(CommonContentContext);
  const [reportsOpen, setReportsOpen] = useState(false);
  const [inventoryManagementOpen, setInventoryManagementOpen] = useState(false);
  const [fertiliserApplicationOpen, setFertiliserApplicationOpen] = useState(false);

  const handleLogout = async (logoutType) => {
    await logout(logoutType);
  };

  const navbarClass = showMenu && navbarWithMenu ? 'fixed' : !showMenu && !navbarWithMenu ? '' : 'hidden';

  //This useEffect handles the click when the navbar is of type fixed.
  useEffect(() => {
    const handleClickOutside = (ev: MouseEvent) => {
      const target = ev.target as Node | null;
      if (navbar.current && target && !navbar.current.contains(target)) {
        setNavbarWithMenu(false);
      }
    };

    if (navbarWithMenu) {
      document.addEventListener('click', handleClickOutside, true);
    } else {
      document.removeEventListener('click', handleClickOutside, true);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [navbarWithMenu]);

  return (
    <div
      ref={navbar}
      id="navbar"
      className={`z-50 h-full w-[250px] min-w-[250px] overflow-y-auto bg-primary font-bold ${navbarClass}`}
    >
      <div className="flex h-full flex-col justify-between gap-[70px] pb-[45px] ">
        <div className="mr-1 mt-1 flex flex-col gap-[25px]">
          <div className={`flex w-full justify-end ${!showMenu && 'hidden'}`}>
            <button
              className="bg-white px-2 text-lg text-primary"
              onClick={() => setNavbarWithMenu(false)}
              data-cy="close-navbar-btn"
            >
              X
            </button>
          </div>
          {/* GrowData Logo */}
          <button
            onClick={() => {
              router.push('/');
              setNavbarWithMenu(false);
            }}
          >
            <img src="/logos/growdata-logo-ops-product-dark-green.png" alt="Growdata logo" />
          </button>

          <div id="content-items-above" className="flex flex-col gap-[18px] pl-[25px]">
            {/* Dashboard */}
            <div className="flex w-full items-center justify-start">
              <button
                onClick={() => {
                  setReportsOpen(false);
                  router.push('/');
                  setNavbarWithMenu(false);
                }}
                className={`group flex h-[40px] w-[200px] cursor-pointer items-center rounded-[10px] pl-[10px] hover:bg-[#F4FAF6] ${
                  router.pathname === '/' ? 'bg-[#F4FAF6] text-primary' : ''
                } `}
              >
                <div className="flex w-[120px] justify-between">
                  <DashboardTraditionalIcon
                    className={`${
                      router.pathname === '/' ? 'fill-primary' : 'fill-[#e8eaed]'
                    } group-hover:fill-primary`}
                  />
                  <p
                    className={`text-star w-[77.11px] group-hover:text-primary ${
                      router.pathname === '/' ? 'text-primary' : 'text-white'
                    }`}
                  >
                    Dashboard
                  </p>
                </div>
              </button>
            </div>

            {/* Enter Data */}
            <div className="flex w-full items-center justify-start">
              <button
                onClick={() => {
                  setReportsOpen(false);
                  router.push('/spray-record');
                  setNavbarWithMenu(false);
                }}
                className={`group flex h-[40px] w-[200px] cursor-pointer items-center rounded-[10px] pl-[10px] hover:bg-[#F4FAF6] ${
                  router.pathname.startsWith('/spray-record') ? 'bg-[#F4FAF6] text-primary' : ''
                } `}
              >
                <div className="flex w-[137px] justify-between">
                  <SprayRecordIcon
                    className={`${
                      router.pathname.startsWith('/spray-record') ? 'fill-primary' : 'fill-[#e8eaed]'
                    } group-hover:fill-primary`}
                  />
                  <p
                    className={`w-[93px] text-start group-hover:text-primary ${
                      router.pathname.startsWith('/spray-record') ? 'text-primary' : 'text-white'
                    }`}
                  >
                    Spray Diary
                  </p>
                </div>
              </button>
            </div>

            {/*Fertiliser Application*/}
            <RoleGuard requiredRoles={[UserRole.SuperAdmin, UserRole.Admin, UserRole.FarmManager]}>
              <div className="flex w-full items-center justify-start">
                <button
                  onClick={() => setFertiliserApplicationOpen(!fertiliserApplicationOpen)}
                  className={`group flex h-[50px] w-[200px] cursor-pointer items-center rounded-[10px] pl-[10px] hover:bg-[#F4FAF6] ${
                    fertiliserApplicationOpen && router.pathname.startsWith('/fertiliser-application')
                      ? 'bg-[#F4FAF6]'
                      : ''
                  } `}
                >
                  <div className="flex w-[120px] items-center justify-between">
                    <InventoryManagementIcon
                      className={`${
                        fertiliserApplicationOpen && router.pathname.startsWith('/fertiliser-application')
                          ? 'fill-primary'
                          : 'fill-[#e8eaed]'
                      } group-hover:fill-primary`}
                    />
                    <p
                      className={`w-[77.11px] whitespace-nowrap text-start group-hover:text-primary ${
                        fertiliserApplicationOpen && router.pathname.startsWith('/fertiliser-application')
                          ? 'text-primary'
                          : 'text-white'
                      }`}
                    >
                      Fertiliser <br /> Application
                    </p>
                  </div>
                </button>
              </div>
            </RoleGuard>

            {fertiliserApplicationOpen && (
              <>
                <div className="flex flex-col gap-[20px] pl-[28px]">
                  <button
                    onClick={() => {
                      router.push('/fertiliser-application/dry-application');
                      setNavbarWithMenu(false);
                    }}
                    className=" group flex h-[28px] w-[185px] cursor-pointer items-center gap-[8px] rounded-[5px] p-2 pl-[5px] hover:bg-[#F4FAF6]"
                  >
                    <PurchaseRecordIcon className={'fill-[#e8eaed] group-hover:fill-primary'} />
                    <p className="whitespace-nowrap text-white group-hover:text-primary">Dry Application</p>
                  </button>
                  <button
                    onClick={() => {
                      router.push('/fertiliser-application/fertigation');
                      setNavbarWithMenu(false);
                    }}
                    className=" group flex h-[28px] w-[185px] cursor-pointer items-center gap-[8px] rounded-[5px] p-2 pl-[5px] hover:bg-[#F4FAF6]"
                  >
                    <PurchaseRecordIcon className="fill-[#e8eaed] group-hover:fill-primary" />
                    <p className="whitespace-nowrap text-white group-hover:text-primary">Fertigation</p>
                  </button>
                </div>
              </>
            )}

            {/* Inventory Management*/}
            <div className="flex w-full items-center justify-start">
              <button
                onClick={() => setInventoryManagementOpen(!inventoryManagementOpen)}
                className={`group flex h-[50px] w-[200px] cursor-pointer items-center rounded-[10px] pl-[10px] hover:bg-[#F4FAF6] ${
                  inventoryManagementOpen && router.pathname.startsWith('/inventory-management') ? 'bg-[#F4FAF6]' : ''
                } `}
              >
                <div className="flex w-[120px] items-center justify-between">
                  <InventoryManagementIcon
                    className={`${
                      inventoryManagementOpen && router.pathname.startsWith('/inventory-management')
                        ? 'fill-primary'
                        : 'fill-[#e8eaed]'
                    } group-hover:fill-primary`}
                  />
                  <p
                    className={`w-[77.11px] whitespace-nowrap text-start group-hover:text-primary ${
                      inventoryManagementOpen && router.pathname.startsWith('/inventory-management')
                        ? 'text-primary'
                        : 'text-white'
                    }`}
                  >
                    Inventory <br /> Management
                  </p>
                </div>
              </button>
            </div>

            {inventoryManagementOpen && (
              <>
                <div className="flex flex-col gap-[20px] pl-[28px]">
                  <button
                    onClick={() => {
                      router.push('/inventory-management/purchase-record');
                      setNavbarWithMenu(false);
                    }}
                    className=" group flex h-[28px] w-[185px] cursor-pointer items-center gap-[8px] rounded-[5px] p-2 pl-[5px] hover:bg-[#F4FAF6]"
                  >
                    <PurchaseRecordIcon className={'fill-[#e8eaed] group-hover:fill-primary'} />
                    <p className="whitespace-nowrap text-white group-hover:text-primary">Purchase Records</p>
                  </button>
                </div>
                <div className="flex flex-col gap-[20px] pl-[28px]">
                  <button
                    onClick={() => {
                      router.push('/inventory-management/transfer-record');
                      setNavbarWithMenu(false);
                    }}
                    className=" group flex h-[28px] w-[185px] cursor-pointer items-center gap-[8px] rounded-[5px] p-2 pl-[5px] hover:bg-[#F4FAF6]"
                  >
                    <PurchaseRecordIcon className={'fill-[#e8eaed] group-hover:fill-primary'} />
                    <p className="whitespace-nowrap text-white group-hover:text-primary">Transfer Records</p>
                  </button>
                </div>
                <RoleGuard requiredRoles={[UserRole.SuperAdmin, UserRole.Admin, UserRole.FarmManager]}>
                  <div className="flex flex-col gap-[20px] pl-[28px]">
                    <button
                      onClick={() => {
                        router.push('/inventory-management/stocktake');
                        setNavbarWithMenu(false);
                      }}
                      className=" group flex h-[28px] w-[185px] cursor-pointer items-center gap-[8px] rounded-[5px] p-2 pl-[5px] hover:bg-[#F4FAF6]"
                    >
                      <PurchaseRecordIcon className={'fill-[#e8eaed] group-hover:fill-primary'} />
                      <p className="whitespace-nowrap text-white group-hover:text-primary">Stocktake</p>
                    </button>
                  </div>
                </RoleGuard>
                <RoleGuard requiredRoles={[UserRole.SuperAdmin, UserRole.Admin, UserRole.FarmManager]}>
                  <div className="flex flex-col gap-[20px] pl-[28px]">
                    <button
                      onClick={() => {
                        router.push('/inventory-management/adjustment');
                        setNavbarWithMenu(false);
                      }}
                      className=" group flex h-[28px] w-[185px] cursor-pointer items-center gap-[8px] rounded-[5px] p-2 pl-[5px] hover:bg-[#F4FAF6]"
                    >
                      <PurchaseRecordIcon className={'fill-[#e8eaed] group-hover:fill-primary'} />
                      <p className="whitespace-nowrap text-white group-hover:text-primary">Adjustments</p>
                    </button>
                  </div>
                </RoleGuard>
              </>
            )}

            {/* Tasks */}
            <div className="hidden w-full items-center justify-start">
              <button className="group flex h-[40px] w-[200px] cursor-pointer items-center rounded-[10px] pl-[10px] hover:bg-[#73cfc8]">
                <div className="flex w-[120px] justify-between">
                  <img className="w-[25px]" src="/icons/tasks-icon.png" alt="Tasks icon" />
                  <p className="w-[77.11px] text-start text-white group-hover:text-primary">Tasks</p>
                </div>
              </button>
            </div>

            {/* Reports */}
            <div className="flex w-full items-center justify-start">
              <button
                onClick={() => setReportsOpen(!reportsOpen)}
                className={`group flex h-[40px] w-[200px] cursor-pointer items-center rounded-[10px] pl-[10px] hover:bg-[#F4FAF6] ${
                  reportsOpen && router.pathname.startsWith('/report') ? 'bg-[#F4FAF6]' : ''
                } `}
              >
                <div className="flex w-[120px] justify-between">
                  <ReportIcon
                    className={`${
                      reportsOpen && router.pathname.startsWith('/report') ? 'fill-primary' : 'fill-[#e8eaed]'
                    } group-hover:fill-primary`}
                  />
                  <p
                    className={`w-[77.11px] text-start group-hover:text-primary ${
                      reportsOpen && router.pathname.startsWith('/report') ? 'text-primary' : 'text-white'
                    }`}
                  >
                    Reporting
                  </p>
                </div>
              </button>
            </div>

            {reportsOpen && (
              <div className="flex flex-col gap-[20px] pl-[28px]">
                <button
                  onClick={() => {
                    router.push('/reports/spray-records');
                    setNavbarWithMenu(false);
                  }}
                  className=" group flex h-[28px] w-[165px] cursor-pointer items-center gap-[20px] rounded-[5px] p-2 pl-[5px] hover:bg-[#F4FAF6]"
                >
                  <ReportCommonIcon className={'fill-[#e8eaed] group-hover:fill-primary'} />
                  <p className="text-white group-hover:text-primary">Spray Diary</p>
                </button>
                <button
                  onClick={() => {
                    router.push('/reports/inventory');
                    setNavbarWithMenu(false);
                  }}
                  className=" group flex h-[28px] w-[165px] cursor-pointer items-center gap-[20px] rounded-[5px] p-2 pl-[5px] hover:bg-[#F4FAF6]"
                >
                  <ReportCommonIcon className={'fill-[#e8eaed] group-hover:fill-primary'} />
                  <p className="text-white group-hover:text-primary">Inventory</p>
                </button>
                <button
                  onClick={() => {
                    router.push('/reports/fertiliser');
                    setNavbarWithMenu(false);
                  }}
                  className="group flex h-[28px] w-[165px] cursor-pointer items-center gap-[20px] rounded-[5px] p-2 pl-[5px] hover:bg-[#F4FAF6]"
                >
                  <ReportCommonIcon className="fill-[#e8eaed] group-hover:fill-primary" />
                  <p className="text-white group-hover:text-primary">Fertiliser</p>
                </button>
              </div>
            )}

            {/* Notes */}
            <div className="hidden w-full items-center justify-start">
              <button className="group flex h-[40px] w-[200px] cursor-pointer items-center rounded-[10px] pl-[10px] hover:bg-[#73cfc8]">
                <div className="flex w-[120px] justify-between">
                  <img className="w-[25px]" src="/icons/notes-icon.png" alt="Notes icon" />
                  <p className="w-[77.11px] text-start text-white group-hover:text-primary">Notes</p>
                </div>
              </button>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-[50px] pb-[20px]">
          <div id="content-items-middle" className="flex flex-col gap-[25px] pl-[25px]">
            <div className="flex w-full flex-col justify-start gap-[20px]">
              {/* Company */}
              <button
                className={`group flex h-[40px] w-[200px] cursor-pointer items-center rounded-[10px] pl-[10px] hover:bg-[#F4FAF6] ${
                  router.pathname.startsWith('/company') ? 'bg-[#F4FAF6] text-primary' : ''
                } `}
                onClick={() => {
                  setReportsOpen(false);
                  router.push('/company');
                  setNavbarWithMenu(false);
                }}
              >
                <div className="flex w-[120px] items-center justify-between">
                  <CompanyIcon
                    className={`${
                      router.pathname.startsWith('/company') ? 'fill-primary' : 'fill-[#e8eaed]'
                    } group-hover:fill-primary`}
                  />
                  <p
                    className={`w-[79px] text-start group-hover:text-primary ${
                      router.pathname.startsWith('/company') ? 'text-primary' : 'text-white'
                    } `}
                  >
                    Company
                  </p>
                </div>
              </button>

              {/* Farm Setup */}
              <button
                className={`group flex h-[40px] w-[200px] cursor-pointer items-center whitespace-nowrap rounded-[10px] pl-[10px] hover:bg-[#F4FAF6] ${
                  farmSetupPaths.some((path) => router.pathname === path || router.pathname.startsWith(`${path}`))
                    ? 'bg-[#F4FAF6] text-primary'
                    : ''
                } `}
                onClick={() => {
                  setReportsOpen(false);
                  router.push('/farm-setup');
                  setNavbarWithMenu(false);
                }}
              >
                <div className="flex w-[120px] justify-between">
                  <SetupIcon
                    className={`${
                      farmSetupPaths.some((path) => router.pathname === path || router.pathname.startsWith(`${path}`))
                        ? 'fill-primary'
                        : 'fill-[#e8eaed]'
                    } group-hover:fill-primary`}
                  />
                  <p
                    className={`w-[79px] text-start group-hover:text-primary ${
                      farmSetupPaths.some((path) => router.pathname === path || router.pathname.startsWith(`${path}`))
                        ? 'text-primary'
                        : 'text-white'
                    }`}
                  >
                    Farm setup
                  </p>
                </div>
              </button>
            </div>
          </div>
          <div id="content-items-below" className="flex flex-col gap-[25px] pl-[25px]">
            {/* My Profile */}
            <div className=" hidden w-full items-center justify-start">
              <button className="group flex h-[40px] w-[200px] cursor-pointer items-center rounded-[10px] pl-[10px] hover:bg-[#73cfc8]">
                <div className="flex w-[120px] justify-between">
                  <img className="w-[25px]" src="/icons/my-profile-icon.png" alt="My Profile icon" />
                  <p className="w-[77.11px] text-start text-white group-hover:text-primary">My Profile</p>
                </div>
              </button>
            </div>

            {/* Settings */}
            <div className=" w-full items-center justify-start">
              <button
                className={`group flex h-[40px] w-[200px] cursor-pointer items-center whitespace-nowrap rounded-[10px] pl-[10px] hover:bg-[#F4FAF6] ${
                  router.pathname.startsWith('/account-settings') || router.pathname.startsWith('/invitation')
                    ? 'bg-[#F4FAF6] text-primary'
                    : ''
                } `}
                onClick={() => {
                  router.push('/account-settings');
                  setNavbarWithMenu(false);
                }}
              >
                <div className="flex w-[120px] justify-between">
                  <AccountSettingIcon
                    className={`${
                      router.pathname.startsWith('/account-settings') || router.pathname.startsWith('/invitation')
                        ? 'fill-primary'
                        : 'fill-[#e8eaed]'
                    } group-hover:fill-primary`}
                  />
                  <p
                    className={`w-[79px] text-start group-hover:text-primary ${
                      router.pathname.startsWith('/account-settings') || router.pathname.startsWith('/invitation')
                        ? 'text-primary'
                        : 'text-white'
                    }`}
                  >
                    Account Settings
                  </p>
                </div>
              </button>
            </div>

            {/* Support */}
            <div className=" hidden w-full items-center justify-start">
              <button className="group flex h-[40px] w-[200px] cursor-pointer items-center rounded-[10px] pl-[10px] hover:bg-[#73cfc8]">
                <div className="flex w-[120px] justify-between">
                  <img className="w-[25px]" src="/icons/support-icon.png" alt="Support icon" />
                  <p className="w-[77.11px] text-start text-white group-hover:text-primary">Support</p>
                </div>
              </button>
            </div>

            {/* Logout */}
            <div className="flex w-full items-center justify-start">
              <button
                onClick={() => handleLogout('redirect')}
                className="group flex h-[40px] w-[200px] cursor-pointer items-center rounded-[10px] pl-[10px] hover:bg-[#F4FAF6]"
              >
                <div className="flex w-[120px] justify-between">
                  <LogoutIcon className={'fill-[#e8eaed] group-hover:fill-primary'} />
                  <p className="w-[77.11px] text-start text-white group-hover:text-primary">Logout</p>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
